import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { Eye, AlertTriangle, CheckCircle, RotateCcw } from 'lucide-react';
import { previewVerification, toUserErrorMessage } from '../../../services/BookkeepingTemplateService';
import TplSelect from '../Common/TplSelect';
import BasAccountPicker from '../BookkeepingTemplates/BasAccountPicker';
import './TemplatePreviewModal.css';

const NUMERIC_TYPES = ['currency_gross', 'currency_net', 'currency', 'number', 'integer'];

const TYPE_LABEL = {
    income: 'intäkt', expense: 'kostnad', transfer: 'överföring', investment: 'investering',
    tax: 'skattepost', payroll: 'lönepost', capital_movement: 'kapitalrörelse',
    year_end: 'bokslutspost', advanced: 'bokföring',
};

/* Seed a test value for one input — example first, then default,
 * then a sensible per-type fallback so the preview is never blank. */
function seedValue(input) {
    if (input.example !== undefined && input.example !== '') return input.example;
    if (input.default !== undefined && input.default !== '') return input.default;
    switch (input.type) {
        case 'currency_gross': case 'currency_net': case 'currency': case 'number': return 1000;
        case 'integer': return 25;
        case 'date': return new Date().toISOString().split('T')[0];
        case 'select': return input.options && input.options[0] ? input.options[0].value : '';
        case 'eu_vat_number': return 'DE123456789';
        default: return '';
    }
}

function seedAll(inputs) {
    const v = {};
    for (const i of inputs) v[i.name] = seedValue(i);
    return v;
}

function formatAmount(n) {
    if (typeof n !== 'number' || !Number.isFinite(n)) return '';
    return n.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* One-line plain-Swedish summary of what the verifikat does. */
function summarize(type, lines, totals) {
    if (!Array.isArray(lines) || lines.length === 0) return null;
    const gross = totals.debit;
    const vat = lines
        .filter((l) => l.account && /^26\d{2}$/.test(l.account))
        .reduce((s, l) => s + l.amount, 0);
    const vatClause = vat > 0 ? ` varav ${formatAmount(vat)} kr moms` : '';
    return `Mallen bokför en ${TYPE_LABEL[type] || 'post'} på ${formatAmount(gross)} kr${vatClause}.`;
}

/* Translate an evaluator error into something an SME owner can act on. */
function friendlyError(e) {
    const raw = e && (e.error || e.message) ? String(e.error || e.message) : '';
    if (!raw) return 'Något stämmer inte — kontrollera testvärdena.';
    if (/imbalance|Σ\s*D\s*=/i.test(raw)) {
        return 'Debet och kredit balanserar inte med dessa värden — kontrollera beloppsformlerna.';
    }
    if (/undeclared input|account ref/i.test(raw)) {
        return 'En rad pekar på en parameter som inte finns. Kontrollera kontoinställningarna på raderna.';
    }
    if (/non-negative/i.test(raw)) return 'En formel gav ett negativt belopp — kontrollera tecknet.';
    if (/required|min|saknas/i.test(raw)) return 'Något testvärde saknas eller har fel format.';
    return raw;
}

/**
 * TemplatePreviewModal — runs the template against editable test
 * values and shows the resulting verifikat in a proper table. Same
 * evaluator (/preview) the verifikat wizard uses, so what's shown here
 * is exactly what a user will get when bookkeeping with the mall.
 *
 * Props:
 *   show       bool
 *   onClose    () => void
 *   companyId  string
 *   body       the template body (effective body from the editor)
 *   accounts   tenant chart of accounts ({ accountNumber, accountName })
 */
function TemplatePreviewModal({ show, onClose, companyId, body, accounts, contract }) {
    const inputs = useMemo(
        () => (body && Array.isArray(body.inputs) ? body.inputs : []),
        [body],
    );
    const [values, setValues] = useState({});
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    // (Re)seed test values from the template's example values each time
    // the modal is opened.
    useEffect(() => {
        if (show) setValues(seedAll(inputs));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const valuesKey = useMemo(() => JSON.stringify(values), [values]);

    // Debounced live preview via the stateless /preview endpoint.
    useEffect(() => {
        if (!show) return undefined;
        if (!body || !Array.isArray(body.lines) || body.lines.length < 2) {
            setResult(null);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const r = await previewVerification(companyId, body, values, contract);
                if (!cancelled) setResult(r);
            } catch (err) {
                if (!cancelled) {
                    setResult({ ok: false, lines: [], totals: { debit: 0, credit: 0 },
                        errors: [{ error: toUserErrorMessage(err) }] });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 400);
        return () => { cancelled = true; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valuesKey, show]);

    const setValue = (name, v) => setValues((p) => ({ ...p, [name]: v }));

    const lines = result && Array.isArray(result.lines) ? result.lines : [];
    const totals = (result && result.totals) || { debit: 0, credit: 0 };
    const errors = result && !result.ok && Array.isArray(result.errors) ? result.errors : [];
    const balanced = !!(result && result.ok);

    return (
        <Modal show={show} onHide={onClose} size="xl" centered dialogClassName="tplpv-modal">
            <Modal.Header closeButton>
                <Modal.Title className="tplpv-title">
                    <Eye size={20} /> Förhandsgranska mall
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="tplpv-body">
                <div className="tplpv-grid">
                    {/* ---- Test values ---- */}
                    <div className="tplpv-pane tplpv-pane--inputs">
                        <div className="tplpv-pane-head">
                            <h4>Testvärden</h4>
                            <button type="button" className="tplpv-reset"
                                onClick={() => setValues(seedAll(inputs))}>
                                <RotateCcw size={13} /> Återställ
                            </button>
                        </div>
                        <p className="tplpv-pane-hint">
                            Ändra värdena för att se hur verifikatet påverkas — precis som när
                            mallen används vid bokföring.
                        </p>
                        {inputs.length === 0 ? (
                            <p className="tplpv-empty">Mallen har inga parametrar.</p>
                        ) : (
                            <div className="tplpv-fields">
                                {inputs.map((input) => (
                                    <TestField key={input.name} input={input}
                                        value={values[input.name]}
                                        accounts={accounts}
                                        onChange={(v) => setValue(input.name, v)} />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* ---- Resulting verifikat ---- */}
                    <div className="tplpv-pane tplpv-pane--result">
                        <div className="tplpv-pane-head">
                            <h4>Verifikat</h4>
                            {result && (
                                <span className={`tplpv-status ${balanced ? 'ok' : 'bad'}`}>
                                    {balanced
                                        ? <><CheckCircle size={14} /> Balanserat</>
                                        : <><AlertTriangle size={14} /> Stämmer inte</>}
                                </span>
                            )}
                        </div>

                        {loading && <p className="tplpv-pane-hint">Beräknar…</p>}

                        {!loading && !result && (
                            <p className="tplpv-empty">
                                Lägg till minst två bokföringsrader för att kunna förhandsgranska.
                            </p>
                        )}

                        {result && balanced && summarize(body.type, lines, totals) && (
                            <p className="tplpv-summary">{summarize(body.type, lines, totals)}</p>
                        )}

                        {errors.length > 0 && (
                            <div className="tplpv-errors" role="alert">
                                <AlertTriangle size={15} />
                                <ul>
                                    {errors.map((e, i) => <li key={i}>{friendlyError(e)}</li>)}
                                </ul>
                            </div>
                        )}

                        {result && lines.length > 0 && (
                            <table className="tplpv-table">
                                <thead>
                                    <tr>
                                        <th>Konto</th>
                                        <th>Benämning</th>
                                        <th className="num">Debet</th>
                                        <th className="num">Kredit</th>
                                        <th>Text</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lines.map((l, i) => (
                                        <tr key={i}>
                                            <td className="acc">{l.account}</td>
                                            <td className="name">
                                                {l.account_name
                                                    || <span className="missing">Konto saknas i kontoplanen</span>}
                                            </td>
                                            <td className="num">
                                                {l.side === 'debit' ? formatAmount(l.amount) : ''}
                                            </td>
                                            <td className="num">
                                                {l.side === 'credit' ? formatAmount(l.amount) : ''}
                                            </td>
                                            <td className="txt">{l.transaction_description || ''}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan={2}>Summa</td>
                                        <td className="num">{formatAmount(totals.debit)}</td>
                                        <td className="num">{formatAmount(totals.credit)}</td>
                                        <td />
                                    </tr>
                                </tfoot>
                            </table>
                        )}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="tplpv-btn" onClick={onClose}>Stäng</button>
            </Modal.Footer>
        </Modal>
    );
}

/* One editable test-value field, widget chosen by input type. */
function TestField({ input, value, accounts, onChange }) {
    const id = `tplpv-${input.name}`;
    let widget;
    if (NUMERIC_TYPES.includes(input.type)) {
        widget = (
            <input id={id} type="number" className="tplpv-input"
                step={input.type === 'integer' ? '1' : '0.01'}
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} />
        );
    } else if (input.type === 'date') {
        widget = (
            <input id={id} type="date" className="tplpv-input" value={value ?? ''}
                onChange={(e) => onChange(e.target.value)} />
        );
    } else if (input.type === 'account_picker') {
        widget = (
            <BasAccountPicker id={id} value={value ?? ''} onChange={onChange}
                accounts={accounts} filter={input.filter} />
        );
    } else if (input.type === 'select') {
        widget = (
            <TplSelect
                options={(input.options || []).map((o) => ({ value: o.value, label: o.label || o.value }))}
                value={value ?? ''} onChange={onChange} placeholder="Välj…" />
        );
    } else {
        widget = (
            <input id={id} type="text" className="tplpv-input" value={value ?? ''}
                placeholder={input.type === 'eu_vat_number' ? 't.ex. DE123456789' : ''}
                onChange={(e) => onChange(e.target.value)} />
        );
    }
    return (
        <div className="tplpv-field">
            <label htmlFor={id} className="tplpv-label">
                {input.label || input.name}
                {input.required ? <span className="tplpv-req"> *</span> : null}
            </label>
            {widget}
        </div>
    );
}

export default TemplatePreviewModal;
